/**
 * Cart Icon
 * Floating cart button with badge count.
 * Renders within the storefront's shadow root.
 */

import { CART_ICON_STYLES } from './cart-styles';
import type { CartManager } from '../../utils/cart-manager';

export interface CartIconOptions {
  shadowRoot: ShadowRoot;
  cartManager: CartManager;
  onClick: () => void;
}

export function createCartIcon(options: CartIconOptions): {
  element: HTMLElement;
  destroy: () => void;
} {
  const { shadowRoot, cartManager, onClick } = options;

  // Inject styles
  const style = document.createElement('style');
  style.textContent = CART_ICON_STYLES;
  shadowRoot.appendChild(style);

  // Create icon button
  const button = document.createElement('button');
  button.className = 'ks-cart-icon';
  button.setAttribute('aria-label', 'Open cart');
  button.innerHTML = `
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
        d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
    </svg>
    <span class="ks-cart-badge ks-hidden">0</span>
  `;

  const badge = button.querySelector('.ks-cart-badge') as HTMLElement;

  function updateBadge() {
    const count = cartManager.getItemCount();
    badge.textContent = count > 99 ? '99+' : String(count);

    if (count > 0) {
      badge.classList.remove('ks-hidden');
      // Trigger bounce animation
      badge.classList.remove('ks-bounce');
      void badge.offsetWidth; // Force reflow
      badge.classList.add('ks-bounce');
    } else {
      badge.classList.add('ks-hidden');
    }
  }

  // Initial state
  updateBadge();

  // Subscribe to cart changes
  const unsubscribe = cartManager.subscribe(() => {
    updateBadge();
  });

  // Click handler
  button.addEventListener('click', onClick);

  shadowRoot.appendChild(button);

  return {
    element: button,
    destroy: () => {
      unsubscribe();
      style.remove();
      button.remove();
    },
  };
}
